import { getCookie } from './cookie';
import { getDeviceParamsFromTracker } from './deviceParams';
import url from './url';

export interface TrackingIdentityOverrides {
    uid?: string;
    pub_uid?: string;
}

type TrackValue =
    | string
    | number
    | boolean
    | object
    | []
    | undefined
    | null;

type TrackParams = Record<string, TrackValue>;

const getTrackQueryParams = (): TrackParams => {
    return {
        ...url.parseQuery()
    };
};

const getTrackRuntime = () => {
    // 注入脚本会在 adv.initAdv 完成后把当时可用的稳定公参缓存到这里，
    // 供业务埋点和性能埋点在后续时机复用。
    return window.__creditCardTrackRuntime;
};

const getLiveAdvParameters = (): TrackParams => {
    return (window.adv?.getMediaData?.() || {}) as TrackParams;
};

const getQueryIdentity = (
    overrides: TrackingIdentityOverrides = {}
): TrackParams => {
    const cookieOrQueryUid = getCookie('uid') || url.get('uid') || '';
    // uid / pub_uid 沿用原有业务兜底顺序：调用方显式覆盖 > cookie > URL。
    const uid = overrides.uid ?? cookieOrQueryUid;
    const pubUid = overrides.pub_uid ?? uid;

    return {
        // queryIdentity 只表示“URL 里直接透传过来的同名身份参数”，
        // 不做 uuid -> outcast_uuid 这类跨语义映射。
        outcast_uuid: url.get('outcast_uuid') || '',
        uid,
        pub_uid: pubUid
    };
};

const getFallbackAdvParameters = (): TrackParams => {
    return {
        // 广告/实验参数统一来源于 adv mediaData 和 URL；
        // 身份类字段单独走 getUserIdentity，避免在这里混入不同语义的兜底。
        // URL 参数可以在 adv 初始化前先拿到，init 完成后的稳定值再由 runtime 补齐。
        ...getLiveAdvParameters(),
        ...((getTrackRuntime()?.advParameters || {}) as TrackParams),
        ...getTrackQueryParams()
    };
};

const getFallbackUserIdentity = (
    overrides: TrackingIdentityOverrides = {}
): TrackParams => {
    // queryIdentity: URL 透传值，最弱兜底，只负责同名字段。
    const queryIdentity = getQueryIdentity(overrides);
    // runtimeIdentity: adv.initAdv 成功后缓存下来的稳定值，用来兜住“当前时刻 live 读取为空”的情况。
    const runtimeIdentity = (getTrackRuntime()?.identityParameters ||
        {}) as TrackParams;
    // liveIdentity: 当前时刻直接从 window.adv 读取的最新值，优先级最高。
    const liveIdentity = {
        outcast_uuid: window.adv?.getAdvUUID?.() ?? '',
        click_id:
            window.adv?.getClientIdByUrl?.(window.adv?.getChannelEnv?.()) ?? ''
    };

    return {
        // 同一语义字段按 “live > runtime > query” 兜底；
        // 这里的三层差异只来自取值时机，不来自字段语义。
        outcast_uuid:
            (liveIdentity.outcast_uuid as string) ||
            (runtimeIdentity.outcast_uuid as string) ||
            (queryIdentity.outcast_uuid as string) ||
            '',
        click_id:
            (liveIdentity.click_id as string) ||
            (runtimeIdentity.click_id as string) ||
            '',
        uid: queryIdentity.uid,
        pub_uid: queryIdentity.pub_uid
    };
};

export const getAdvParameters = (): TrackParams => {
    try {
        if (window.getAdvParameters) {
            return window.getAdvParameters() as TrackParams;
        }
    } catch (error) {
        console.error('getAdvParameters error:', error);
    }

    return getFallbackAdvParameters();
};

export const getUserIdentity = (
    overrides: TrackingIdentityOverrides = {}
): TrackParams => {
    try {
        if (window.getUserIdentity) {
            return window.getUserIdentity(overrides) as TrackParams;
        }
    } catch (error) {
        console.error('getUserIdentity error:', error);
    }

    return getFallbackUserIdentity(overrides);
};

export const getPageTrackingConfig = () => {
    return {
        pageId: window.__PAGE_CONFIG__?.pageId ?? '',
        trackingPageId: window.__PAGE_CONFIG__?.trackingPageId ?? '',
        pageVersion: window.__PAGE_CONFIG__?.pageVersion ?? 1
    };
};

export const buildCreditCardCommonParams = ({
    pubPage,
    uid,
    pub_uid,
    ext,
}: {
    pubPage?: string;
    uid?: string;
    pub_uid?: string;
    ext?: TrackParams;
} = {}): TrackParams => {
    const pageConfig = getPageTrackingConfig();
    const identity = getUserIdentity({ uid, pub_uid });

    return {
        ...getAdvParameters(),
        ...getDeviceParamsFromTracker(),
        pub_biz_line: 'fin',
        pub_subbiz: 'creditcard',
        // 业务埋点统一使用页面 pageId；性能埋点在初始化脚本里单独使用 trackingPageId。
        pub_page: pubPage ?? window.location.href,
        page_id: pageConfig.pageId,
        page_version: pageConfig.pageVersion,
        apply_source: 'OUT',
        click_id: identity.click_id,
        outcast_uuid: identity.outcast_uuid,
        uid: identity.uid,
        pub_uid: identity.pub_uid,
        ext: {
            ...getTrackQueryParams(),
            outcast_uuid: identity.outcast_uuid,
            ...ext
        }
    };
};
